/**
 * BC5 subtasks (Kanban::Step) on parent todo viewer
 */

import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, parseLocalDate } from '@/lib/utils';
import { basecampApi } from '@/lib/basecamp-api';
import { testIds } from '@/lib/test-ids';
import { toast } from 'sonner';
import type { BasecampTodo, BasecampTodoStep } from '@/types/basecamp';

function formatDueShort(dueOn: string | undefined): string {
  if (!dueOn) return '';
  const due = parseLocalDate(dueOn);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const diff = Math.round((due.getTime() - todayStart.getTime()) / 86400000);
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 0) return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (diff <= 7) return `In ${diff}d`;
  return due.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function normalizeSteps(todo: BasecampTodo): BasecampTodoStep[] {
  const raw = todo.steps;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((step) => ({
    ...step,
    id: String(step.id),
    title: step.title || (step as { content?: string }).content || 'Untitled',
  }));
}

interface TodoSubtasksSectionProps {
  todo: BasecampTodo;
  projectId?: string;
  highlightStepId?: string;
  onStepsUpdated?: () => void | Promise<void>;
}

export function TodoSubtasksSection({
  todo,
  projectId,
  highlightStepId,
  onStepsUpdated,
}: TodoSubtasksSectionProps) {
  const steps = normalizeSteps(todo);
  const highlightRef = useRef<HTMLLIElement>(null);
  const [togglingStepId, setTogglingStepId] = useState<string | null>(null);
  const [localCompleted, setLocalCompleted] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!highlightStepId || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightStepId, steps.length]);

  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const step of steps) {
      next[step.id] = !!step.completed;
    }
    setLocalCompleted(next);
  }, [todo.steps]);

  if (steps.length === 0) return null;

  const handleToggle = async (step: BasecampTodoStep) => {
    if (!projectId) return;
    const stepId = String(step.id);
    const nextCompleted = !(localCompleted[stepId] ?? step.completed);
    setTogglingStepId(stepId);
    setLocalCompleted((prev) => ({ ...prev, [stepId]: nextCompleted }));

    try {
      await basecampApi.setStepCompletion(projectId, stepId, nextCompleted);
      await onStepsUpdated?.();
    } catch (err) {
      setLocalCompleted((prev) => ({ ...prev, [stepId]: !nextCompleted }));
      toast.error('Could not update subtask', {
        description: err instanceof Error ? err.message : 'Try again or refresh from Basecamp',
      });
    } finally {
      setTogglingStepId(null);
    }
  };

  return (
    <section data-testid={testIds.basecamp.todoSubtasksSection} className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">Subtasks</h3>
        <Badge variant="secondary" className="text-xs px-1.5 py-0">
          {steps.length}
        </Badge>
      </div>
      <ul className="space-y-1" data-testid={testIds.basecamp.todoSubtasksList}>
        {steps.map((step) => {
          const stepId = String(step.id);
          const isHighlighted = highlightStepId != null && stepId === String(highlightStepId);
          const assignees = step.assignees ?? [];
          const dueLabel = formatDueShort(step.due_on);
          const isCompleted = localCompleted[stepId] ?? !!step.completed;
          const isToggling = togglingStepId === stepId;

          return (
            <li
              key={step.id}
              ref={isHighlighted ? highlightRef : undefined}
              data-testid={testIds.basecamp.todoSubtaskRow(stepId)}
              className={cn(
                'flex items-center gap-3 rounded-md px-2 py-2 -mx-2',
                isHighlighted && 'bg-primary/10 ring-1 ring-primary/30'
              )}
            >
              <Checkbox
                  checked={isCompleted}
                  disabled={isToggling || !projectId}
                  aria-label={isCompleted ? 'Mark subtask incomplete' : 'Mark subtask complete'}
                  onCheckedChange={() => { void handleToggle(step); }}
                  data-testid={testIds.basecamp.todoSubtaskCheckbox(stepId)}
                  className="shrink-0"
                />
              <span
                className={cn(
                  'flex-1 min-w-0 text-sm',
                  isCompleted && 'text-muted-foreground line-through'
                )}
              >
                {step.title}
              </span>
              <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                {assignees.map((a) => (
                  <Badge
                    key={String(a.id)}
                    variant="outline"
                    className="text-sm px-2 py-0.5 font-normal gap-1.5"
                  >
                    {a.avatar_url ? (
                      <img src={a.avatar_url} alt="" className="h-4 w-4 rounded-full" />
                    ) : null}
                    {a.name.split(' ')[0]}
                  </Badge>
                ))}
                {dueLabel ? (
                  <Badge variant="secondary" className="text-sm px-2 py-0.5 font-normal gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {dueLabel}
                  </Badge>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
